import {
    type AuthenticationMethod,
    OAuthError,
    PopupCancelledError,
    useAuth0,
} from "@auth0/auth0-react";
import { useCallback, useRef, useState } from "react";

import { showAPIErrorMessage } from "utilities/apiHandler";
import { MY_ACCOUNT_STEP_UP_MAX_AGE_SECONDS } from "utilities/auth0MyAccount/constants";

/** True when an Auth0 error means the elevated session is no longer valid (reactive expiry). */
const isLoginRequired = (e: unknown): boolean =>
    e instanceof OAuthError && e.error === "login_required";

export interface IUseMyAccountMfa {
    factors: AuthenticationMethod[] | undefined;
    isElevated: boolean;
    isLoading: boolean;
    /** Interactive popup that re-authenticates within the 10-min window, then loads factors. */
    prime: () => Promise<void>;
    /** Deletes a factor after confirming elevation, then refreshes the list. */
    removeFactor: (id: string) => Promise<void>;
}

export const useMyAccountMfa = (): IUseMyAccountMfa => {
    const { getAccessTokenWithPopup, myAccount } = useAuth0();
    const [factors, setFactors] = useState<AuthenticationMethod[] | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    // Wall-clock ms when elevation was granted; null when locked. A ref (not state) because it is
    // read inside async callbacks that must see the latest value without a re-render dependency.
    const elevatedAtRef = useRef<number | null>(null);
    // Mirror into state purely to drive re-renders when lock/unlock changes.
    const [isElevated, setIsElevated] = useState(false);

    const lock = useCallback(() => {
        elevatedAtRef.current = null;
        setIsElevated(false);
        // Deliberately do NOT clear `factors`. The list is gated on `isElevated`, so a stale array is
        // never rendered; keeping it lets the island distinguish "never loaded" (factors undefined →
        // "verify your identity") from "session expired" (factors present → "re-verify") copy.
    }, []);

    // Proactive expiry: callers check before every mutation. Returns false (and locks) once the
    // elevation window has elapsed, so a mutation never fires on a stale token.
    const checkElevation = useCallback((): boolean => {
        const at = elevatedAtRef.current;
        if (at === null) {
            return false;
        }
        const elapsedSeconds = (Date.now() - at) / 1000;
        if (elapsedSeconds >= MY_ACCOUNT_STEP_UP_MAX_AGE_SECONDS) {
            lock();
            return false;
        }
        return true;
    }, [lock]);

    const listFactors = useCallback(async (): Promise<void> => {
        setIsLoading(true);
        try {
            const result = await myAccount.getAuthenticationMethods();
            setFactors(result);
        } catch (e) {
            if (isLoginRequired(e)) {
                lock(); // reactive expiry — flip to locked instead of surfacing a raw error
            } else {
                showAPIErrorMessage(e);
            }
        } finally {
            setIsLoading(false);
        }
    }, [myAccount, lock]);

    const prime = useCallback(async (): Promise<void> => {
        setIsLoading(true);
        try {
            await getAccessTokenWithPopup({
                authorizationParams: { max_age: MY_ACCOUNT_STEP_UP_MAX_AGE_SECONDS },
            });
            elevatedAtRef.current = Date.now();
            setIsElevated(true);
            await listFactors();
        } catch (e) {
            // User-cancelled popup (PopupCancelledError) or login_required: stay locked, no toast
            // for a deliberate cancel. Surface anything else.
            lock();
            if (!isLoginRequired(e) && !(e instanceof PopupCancelledError)) {
                showAPIErrorMessage(e);
            }
        } finally {
            setIsLoading(false);
        }
    }, [getAccessTokenWithPopup, listFactors, lock]);

    const removeFactor = useCallback(
        async (id: string): Promise<void> => {
            if (!checkElevation()) {
                return; // proactive expiry — locked; UI shows re-verify
            }
            setIsLoading(true);
            try {
                await myAccount.deleteAuthenticationMethod(id);
                await listFactors();
            } catch (e) {
                if (isLoginRequired(e)) {
                    lock(); // reactive expiry mid-call
                } else {
                    showAPIErrorMessage(e);
                }
            } finally {
                setIsLoading(false);
            }
        },
        [checkElevation, myAccount, listFactors, lock],
    );

    return { factors, isElevated, isLoading, prime, removeFactor };
};
